<template>
  <div class="container py-4 cohost-page">
    <div class="row justify-content-center">
      <div class="col-lg-9">
        <div class="cohost-card shadow-sm">
          <div class="cohost-header">
            <div class="d-flex align-items-center gap-2">
              <BackButton class="btn btn-link p-0 mr-2" fallback="/host">
                <i class="fas fa-chevron-left"></i>
              </BackButton>
              <div>
                <p class="eyebrow mb-1">Roles</p>
                <div class="page-title mb-0">Manage Co-Hosts</div>
              </div>
            </div>
            <button
              class="btn btn-primary btn-pill cohost-save-btn ml-auto"
              @click="saveCoHosts"
              :disabled="!hasChanges || saving"
            >
              <span v-if="!saving">
                <i class="fas fa-save mr-2"></i>
                <span class="btn-label">Save</span>
              </span>
              <span v-else>
                <span class="spinner-border spinner-border-sm mr-2"></span>
                <span class="btn-label">Saving…</span>
              </span>
            </button>
          </div>

          <div class="cohost-body">
            <!-- Search -->
            <div class="search-wrap mb-3">
              <i class="fas fa-search"></i>
              <input
                type="text"
                class="form-control search-input"
                placeholder="Search co-hosts by name, username, email or phone"
                v-model="searchQuery"
              />
            </div>

            <!-- List -->
            <div class="list-shell">
              <div v-if="loading" class="text-center py-4">
                <div class="spinner-border text-primary" role="status">
                  <span class="sr-only">Loading...</span>
                </div>
              </div>

              <div v-else-if="filteredCoHosts.length === 0" class="text-center py-4 text-muted">
                No co-hosts found.
              </div>

              <div v-else>
                <ul class="list-unstyled cohost-list">
                  <li
                    v-for="cohost in filteredCoHosts"
                    :key="cohost.uid"
                    class="cohost-row"
                    :class="{ selected: rowIsSelected(cohost) }"
                  >
                    <div class="d-flex align-items-center">
                      <img
                        class="avatar"
                        :src="cohost.user_img || 'https://dummyimage.com/200x200/222/fff.png?text=A'"
                        :alt="cohost.name"
                      />
                      <div class="cohost-meta flex-grow-1">
                        <p class="name mb-1 text-truncate">{{ formatName(cohost.name) || '—' }}</p>
                        <p class="handle mb-0 text-truncate">@{{ cohost.username }}</p>
                      </div>
                    </div>

                    <div class="cohost-actions">
                      <!-- Already added -> selectable Remove -->
                      <button
                        v-if="isAlreadyAdded(cohost)"
                        class="icon-chip"
                        :class="cohost.pending === 'remove' ? 'chip-remove' : 'chip-selected'"
                        role="button"
                        :aria-pressed="cohost.pending === 'remove'"
                        @click="toggleRemove(cohost)"
                        @keydown.enter.prevent="toggleRemove(cohost)"
                        @keydown.space.prevent="toggleRemove(cohost)"
                        :title="cohost.pending === 'remove' ? 'Undo removal' : 'Remove co-host on save'"
                      >
                        <i :class="cohost.pending === 'remove' ? 'fas fa-undo' : 'fas fa-check'"></i>
                      </button>

                      <!-- Not added -> selectable Add -->
                      <button
                        v-else
                        class="icon-chip"
                        :class="cohost.pending === 'add' ? 'chip-selected' : 'chip-add'"
                        role="button"
                        :aria-pressed="cohost.pending === 'add'"
                        @click="toggleAdd(cohost)"
                        @keydown.enter.prevent="toggleAdd(cohost)"
                        @keydown.space.prevent="toggleAdd(cohost)"
                        :title="cohost.pending === 'add' ? 'Undo add' : 'Add as co-host on save'"
                      >
                        <i :class="cohost.pending === 'add' ? 'fas fa-check' : 'fas fa-plus'"></i>
                      </button>
                    </div>
                  </li>
                </ul>
              </div>
              <!-- /Items -->
            </div>
          </div><!-- /body -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/services/api';
import { useSessionStore } from '@/stores/session';
import BackButton from '@/components/common/BackButton.vue';

export default {
  name: 'CoHostList',
  components: { BackButton },
  setup() {
    const route = useRoute();
    const session = useSessionStore();

    // Robust event id derivation (handles /cohost/:id and variants)
    const deriveEventId = (r) => {
      const cand = [
        r.params?.eventid, r.params?.eventId, r.params?.id,
        r.query?.eventid,  r.query?.eventId,  r.query?.id
      ].find(v => v != null && String(v).trim() !== '');
      if (cand) return String(cand);
      const last = (r.path || r.fullPath || '')
        .split('?')[0].split('/').filter(Boolean).pop();
      return last && /^\d+$/.test(last) ? last : null;
    };

    const eventId = ref(deriveEventId(route));
    watch(() => route.fullPath, () => { eventId.value = deriveEventId(route); });

    const cohosts = ref([]);
    const loading = ref(true);
    const saving  = ref(false);
    const searchQuery = ref('');

    // UID builder to avoid duplicate-id selection bugs
    const makeUid = (g, idx) => {
      const safe = (v) => (v == null ? 'nil' : String(v).trim());
      return ['ev', eventId.value, 'id', safe(g.id), 'u', safe(g.username), 'e', safe(g.email), 'i', idx].join(':');
    };

    const filteredCoHosts = computed(() => {
      if (!searchQuery.value) return cohosts.value;
      const q = searchQuery.value.toLowerCase();
      return cohosts.value.filter((c) => {
        const name = (c.name || '').toLowerCase();
        const username = (c.username || '').toLowerCase();
        const email = (c.email || '').toLowerCase();
        const phone = String(c.phone || '').toLowerCase();
        return name.includes(q) || username.includes(q) || email.includes(q) || phone.includes(q);
      });
    });

    const isAlreadyAdded = (c) => Number(c.add_status) === 1 || c.invited === true;
    const formatName = (name) => {
      if (!name) return '';
      return String(name)
        .split(' ')
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    };

    // Row is part of the FINAL selection if:
    // - was already added and NOT marked to remove, OR
    // - was not added and IS marked to add
    const rowIsSelected = (c) =>
      (isAlreadyAdded(c) && c.pending !== 'remove') ||
      (!isAlreadyAdded(c) && c.pending === 'add');

    const hasChanges = computed(() => cohosts.value.some((c) => c.pending === 'add' || c.pending === 'remove'));

    const finalSelectedIds = computed(() =>
      cohosts.value
        .filter(rowIsSelected)
        .map((c) => c.id)
        .filter(Boolean)
    );

    async function loadCoHosts() {
      try {
        loading.value = true;

        const fd = new FormData();
        fd.append('userid', session.userId);
        fd.append('eventid', eventId.value);

        const res = await api.getCoHostsList(fd);
        const payload = res?.data ?? res;
        const ok = payload?.status === 'ok' || payload?.status === true || res?.status === true;

        const list =
          (ok && Array.isArray(payload?.data) && payload.data) ||
          (ok && Array.isArray(payload?.list) && payload.list) ||
          [];

        cohosts.value = list.map((g, idx) => ({
          ...g,
          uid: makeUid(g, idx),
          invited: Number(g?.add_status) === 1 || g?.invited === true,
          pending: null, // 'add' | 'remove' | null
        }));
      } catch (e) {
        console.error('Error loading cohosts:', e);
        cohosts.value = [];
      } finally {
        loading.value = false;
      }
    }

    function toggleAdd(c) {
      if (isAlreadyAdded(c)) return; // can't add an already-added row
      cohosts.value = cohosts.value.map((x) =>
        x.id === c.id ? { ...x, pending: x.pending === 'add' ? null : 'add' } : x
      );
    }
    function toggleRemove(c) {
      if (!isAlreadyAdded(c)) return; // can't remove a not-added row
      cohosts.value = cohosts.value.map((x) =>
        x.id === c.id ? { ...x, pending: x.pending === 'remove' ? null : 'remove' } : x
      );
    }

    async function saveCoHosts() {
      if (!hasChanges.value || saving.value) return;

      try {
        saving.value = true;
        const ids = [...new Set(finalSelectedIds.value.map(String))];

        const fd = new FormData();
        fd.append('userid', session.userId);
        fd.append('eventid', eventId.value);
        fd.append('host_ids', ids.join(',')); // server receives the final set

        const res = await api.updateCohost(fd);
        const ok = res?.status === true || (res?.data?.status === true) || (res?.data?.status === 'ok');
        if (!ok) throw new Error(res?.message || res?.data?.message || 'Failed to save cohosts');

        // Reflect saved state locally
        const keep = new Set(ids);
        cohosts.value = cohosts.value.map((c) => ({
          ...c,
          add_status: keep.has(String(c.id)) ? 1 : 0,
          invited: keep.has(String(c.id)),
          pending: null,
        }));

        alert('Co-hosts saved!');
      } catch (e) {
        console.error('saveCoHosts error:', e);
        alert('Error saving co-hosts: ' + (e.message || 'Please try again.'));
      } finally {
        saving.value = false;
      }
    }

    onMounted(loadCoHosts);

    return {
      // route
      eventId,
      // state
      cohosts,
      loading,
      saving,
      searchQuery,
      // computed & helpers
      filteredCoHosts,
      formatName,
      isAlreadyAdded,
      rowIsSelected,
      hasChanges,
      finalSelectedIds,
      // actions
      toggleAdd,
      toggleRemove,
      saveCoHosts,
    };
  },
};
</script>

<style>
.cohost-page{max-width:1080px;}
.cohost-card{border:1px solid #eef1f6;border-radius:14px;background:#fff;}
.cohost-header{display:flex;align-items:center;justify-content:space-between;gap:1rem;padding:1rem 1.25rem;border-bottom:1px solid #f0f2f6;}
.cohost-body{padding:1.25rem;}
.eyebrow{text-transform:uppercase;font-size:.75rem;letter-spacing:.05em;color:#6b7280;font-weight:700;}
.btn-pill{border-radius:999px;padding:.55rem 1.15rem;font-weight:700;}

.search-wrap{display:flex;align-items:center;gap:.6rem;border:1px solid #e5e7eb;border-radius:12px;padding:.65rem .75rem;background:#f8fafc;}
.search-wrap i{color:#6b7280;}
.search-input{border:0;background:transparent;box-shadow:none;}
.search-input:focus{outline:none;box-shadow:none;}

.list-shell{border:1px solid #eef1f6;border-radius:12px;overflow:hidden;background:#fff;}
.cohost-list{margin:0; padding:0;}
.cohost-row{
  display:grid;
  grid-template-columns: 1fr auto;
  gap: .75rem;
  align-items:center;
  padding:.9rem 1rem;
  border-bottom:1px solid #f1f2f5;
  transition:background .15s ease, border-color .15s ease;
}
.cohost-row:last-child{border-bottom:0;}
.cohost-row.selected{background:#f5f9ff;border-color:#e0ecff;}
.avatar{
  width:48px;height:48px;border-radius:50%;
  object-fit:cover;box-shadow:0 2px 6px rgba(0,0,0,.12);
  margin-right:.75rem;flex-shrink:0;
}
.cohost-meta .name{font-weight:700;font-size:1rem;}
.cohost-meta .handle{color:#6b7280;font-size:.9rem;}
.cohost-actions{
  display:flex;
  gap:.5rem;
  flex-wrap:nowrap;
  justify-content:flex-end;
  align-items:center;
  margin-left:auto;
}

.icon-chip{
  width:38px;height:38px;
  border-radius:50%;border:1px solid #e5e7eb;
  display:inline-flex;align-items:center;justify-content:center;
  background:#fff;color:#374151;
  transition:all .15s ease;
}
.icon-chip:hover{box-shadow:0 4px 10px rgba(0,0,0,.08);}
.chip-selected{background:#fff7ed;border-color:#fbbf24;color:#d97706;}
.chip-remove{background:#fee2e2;border-color:#fecdd3;color:#b91c1c;}
.chip-add{background:#f8fafc;border-color:#e5e7eb;color:#0f172a;}

@media (max-width: 575px){
  .cohost-header{flex-direction:column;align-items:flex-start;}
  .cohost-save-btn{align-self:flex-end;}
  .cohost-row{grid-template-columns: 1fr auto;align-items:center;}
  .cohost-actions{justify-content:flex-end;flex-wrap:nowrap;}
  .btn-pill{width:100%;text-align:center;padding:.5rem .65rem;}
  .btn-pill .btn-label{display:none;}
  .btn-pill i{margin-right:0 !important;}
}
</style>
